import { Group, Stack, TextInput, useMantineTheme } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form/lib/types';
import { Icon, type IconNames, Text } from '@repo/foundations';
import { CustomSelector } from '../../CustomSelector';
import EmojiIconSelector from '../../EmojiIconSelector';
import type { ITeamForm } from './TeamForm.constants';

export function TeamForm({ form }: { form: UseFormReturnType<ITeamForm> }) {
	const theme = useMantineTheme();

	return (
		<Stack spacing="xl">
			<Stack spacing={4}>
				<Text fw={500} size="sm">
					Icon & name
				</Text>
				<Group spacing={8} align="start">
					<EmojiIconSelector
						icon={form.getInputProps('icon').value}
						title="teams-icon"
						onChange={(emoji) => form.getInputProps('icon').onChange(emoji)}
						iconProps={{
							variant: 'outline',
							// This 36px matches the current height of the mantine text input pretheming.
							h: '36px',
							w: '36px',
							sx: {
								paddingLeft: 5,
								borderColor: theme.other.getColor('border/secondary/default'),
							},
						}}
					/>
					<TextInput
						sx={{ flexGrow: 1 }}
						size="sm"
						placeholder="e.g. Marketing"
						{...form.getInputProps('name')}
					/>
				</Group>
			</Stack>
			<Stack spacing={4}>
				<Text fw={500} size="sm">
					Permissions
				</Text>
				<Group spacing={8}>
					<CustomSelector
						menuProps={{
							width: 'target',
							position: 'bottom-start',
							withinPortal: true,
						}}
						targetProps={{ w: '100%' }}
						items={[
							{
								value: 'OPEN',
								label: 'Public',
								description: 'Anyone can see, browse, and join this team',
								iconName: 'users' as IconNames,
							},
							{
								value: 'CLOSED',
								label: 'Private',
								description:
									'Anyone can see this team, but access requires admin approval',
								iconName: 'lock' as IconNames,
							},
						]}
						renderItem={(item) => (
							<Group align="center" py={8} px={12} spacing={8} noWrap>
								<Icon name={item.iconName} />
								<Stack spacing={0}>
									<Text size="sm">{item.label}</Text>
									<Text size="xs" color="text/secondary/default">
										{item.description}
									</Text>
								</Stack>
							</Group>
						)}
						{...form.getInputProps('type')}
					/>
				</Group>
			</Stack>
		</Stack>
	);
}
